<template>
  <UserCommonLayout :main-class-name="'pt0'" :footer-class-name="'line'">
    <template v-if="!isLoading" #content>
      <LazyProSkillManagerCommonBreadCrumb
          :path-name="t('skillMonitor-candidate-report')"
          :assessment-name="reportInfo.title || ''"
      />
      <LazyProSkillManagerReportBanner
          :user-data="reportInfo.userInfo"
          :user-id="uuid"
          :assessment-id="assessmentId"
      />
      <LazyProSkillManagerReportTab
          :tab-type="detailTab"
          :is-visible-analysis-tab="isVisibleAnalysisTab"
      />
      <LazyProSkillManagerReportDashBoard
          :tab-type="detailTab"
          :assessmentSummary="assessmentSummary"
          :trackCandidateSummaries="trackSummary"
          :skillCandidateSummaries="skillSummary"
          :action-analysis="candidateActionAnalysis"
          :assessment-id="assessmentId"
          :user-data="reportInfo.userInfo"
          :user-uuid="uuid"
          :analysis-tasks="analysisTasksData"
          :assessment-tasks="assessmentTasksData"
          :is-unsubmitted="reportInfo.isUnsubmitted"
      />
    </template>
  </UserCommonLayout>
</template>

<script lang="ts" setup>
import {
  LazyProSkillManagerCommonBreadCrumb,
  LazyProSkillManagerReportBanner,
  LazyProSkillManagerReportDashBoard,
  LazyProSkillManagerReportTab
} from '#components';
import {useNuxtApp} from '#imports';
import {useI18n} from 'vue-i18n';
import {useLoadingStore} from '~~/application/store/loading/loadingStore';
import DialogType from '~~/application/components/Common/Dialog/model/DialogType';
import { useGenericDialog } from '~/composables/useGenericDialog';
import {useDialogStore} from '~~/application/store/dialog/dialogStore';
import {ReportTabType} from '~~/package/mapper/report/model/ReportTabType';
import {ref} from 'vue';
import type {SkillSummaryModel} from '~~/package/mapper/report/model/assessment/SkillSummaryModel';
import {ReportDomainType} from '~~/package/mapper/report/model/ReportDomainType';
import {useReportService} from '~/composables/service/useReportService';
import {
  AssessmentCandidateSummaryModel
} from '~~/package/mapper/report/model/candidate/AssessmentCandidateSummaryModel';
import {TrackCandidateSummaryModel} from '~~/package/mapper/report/model/candidate/TrackCandidateSummaryModel';
import {useProSkillManagerService} from '~/composables/service/useProSkillManagerService';
import {CandidateActionAnalysisModel} from '~~/package/mapper/report/model/candidate/CandidateActionAnalysisModel';
import {ProSkillMangerReportModel} from '~~/package/mapper/proSkillManager/model/report/ProSkillManagerReportModel';
import {useUserStore} from '~/store/user/userStore';
import {useCertifyStaticAnalysisService} from '~/composables/service/useCertifyStaticAnalysisService';

const { t } = useI18n();

const { $i18n } = useNuxtApp();
const lang = computed(() =>
  $i18n.locale.value.toUpperCase()
);
const route = useRoute();
const router = useRouter();

const proSkillManagerService = useProSkillManagerService();
const reportService = useReportService();
const userStore = useUserStore();
const certifyStaticAnalysisService = useCertifyStaticAnalysisService();
const loadingStore = useLoadingStore();
const dialogStore = useDialogStore();
const { guard } = usePermissionGuard();
const { openGenericAlert } = useGenericDialog();

const isLoading = ref(true);
const uuid = route.query.uuid as string;
const assessmentId = ref(parseInt(route.query.assessmentId as string));
const detailTab = ref<ReportTabType>(ReportTabType.ASSESSMENT);

const reportInfo = ref<ProSkillMangerReportModel>(new ProSkillMangerReportModel());
const assessmentSummary = ref<AssessmentCandidateSummaryModel>(new AssessmentCandidateSummaryModel());
const trackSummary = ref<TrackCandidateSummaryModel[]>([]);
const skillSummary = ref<SkillSummaryModel[]>([]);
const candidateActionAnalysis = ref<CandidateActionAnalysisModel>(new CandidateActionAnalysisModel());
const analysisTasksData = ref<Array<{taskId: string, files: string[]}>>([]);
const assessmentTasksData = ref<Array<{trackTitle: string, trackLevelTitle: string, taskIds: number[]}>>([]);


// ASMT-REPORT-06: SW 품질 분석 탭은 정적분석 결과 task가 1개 이상일 때만 노출
const isVisibleAnalysisTab = computed(() => {
  return analysisTasksData.value.length > 0;
})

// ASMT-REPORT-12: S3 정적분석 summary 파일 조회 + 평가 taskId 교차검증
const checkAnalysisSummaryFiles = async () => {
  try {
    // S3에서 분석 파일 조회
    const s3TasksData = await certifyStaticAnalysisService.getAllTasksAnalysis(
      assessmentId.value,
      uuid
    );

    // API에서 assessment tasks 조회 (sample인 경우 mock 데이터 반환)
    const apiTasksData = await proSkillManagerService.getAssessmentTasks(assessmentId.value);
    assessmentTasksData.value = apiTasksData;

    // API에서 조회한 모든 taskId 목록
    const validTaskIds = new Set<string>();
    apiTasksData.forEach(track => {
      track.taskIds.forEach(taskId => {
        validTaskIds.add(String(taskId));
      });
    });

    // S3의 taskId가 API taskIds와 매칭되는 것만 필터링
    const matchedTasks = s3TasksData.filter(task => validTaskIds.has(task.taskId));

    analysisTasksData.value = matchedTasks;
  } catch (error) {
    console.error('Failed to load analysis data:', error);
    analysisTasksData.value = [];
    assessmentTasksData.value = [];
  }
}

const initData = () => {
  Promise.all([
        proSkillManagerService.getProSkillManagerReport(lang.value, uuid, assessmentId.value),
        reportService.getReportCandidateAssessmentSummary(uuid, ReportDomainType.CUSTOM_ASSESSMENT, assessmentId.value),
        reportService.getReportCandidateTrackSummary(uuid, ReportDomainType.CUSTOM_ASSESSMENT, assessmentId.value),
        reportService.getReportCandidateSkillSummary(lang.value, uuid, ReportDomainType.CUSTOM_ASSESSMENT, assessmentId.value),
        reportService.getReportCandidateActionAnalysis(uuid, ReportDomainType.CUSTOM_ASSESSMENT, assessmentId.value)
      ]
  ).then(async resolve => {
    reportInfo.value = resolve[0]
    assessmentSummary.value = resolve[1];
    trackSummary.value = resolve[2];
    skillSummary.value = resolve[3];
    candidateActionAnalysis.value = resolve[4];

    // S3에서 summary 파일 확인
    await checkAnalysisSummaryFiles();

    isLoading.value = false;
    loadingStore.closeLoading();
  }).catch(e => {
    if([404].includes(e.response.status)) {
      router.replace("/404");
      loadingStore.closeLoading();
    }
    else if([500].includes(e.response.status)) {
      router.replace("/500");
      loadingStore.closeLoading();
    }
    if (e.response?.status === 401){
      openDoNotHavePermissionPopup();
    }
  });
};

const openDoNotHavePermissionPopup = () => {
  openGenericAlert(
    { title: t("skillMonitor-main-limit-permission-title"), contents: t("skillMonitor-main-limit-permission-description"), popupClass: 'dark' },
    {
      confirm: { title: t("common-ok"), event: () => router.push({name: "dashboard-proSkillManager"}) },
      close: { event: () => router.push({name: "dashboard-proSkillManager"}) }
    }
  );
}

const handlerAssessmentMenuDoNotHavePermission = () => {
  if (!guard(
      userStore.isPermissionManageAssessment ||
      userStore.isSuperAdminType
  )) return;
}

onMounted(() => {
  loadingStore.openLoading("");
  if(assessmentId.value === 0) dialogStore.open({ type: DialogType.ProSkillAssureSamplePopup, props: { isLightMode: true } });
  handlerAssessmentMenuDoNotHavePermission();
  initData();
});

watch(() => route.query, () => {
  if(assessmentId.value === 0) {
    dialogStore.open({ type: DialogType.ProSkillAssureSamplePopup, props: { isLightMode: true } });
  }

  if(route.query.tab) {
    detailTab.value = route.query.tab as ReportTabType
  }
}, {immediate: true})
</script>

<style>
@import "~/assets/scss/business_style.scss";

.unsubmitted-report-notice {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 24px;
  text-align: center;
}

.unsubmitted-report-notice p {
  font-size: 16px;
  color: #6b7280;
  line-height: 1.6;
}
</style>